<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <div class="row mt-3 overflow-scroll">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <tab-rail class="mb-3" :tabs="tabs" :value="activeTabIndex" @input="switchTab">
            <template #actions>
              <template v-if="activeTabIndex === 0 || activeTabIndex === 2">
                <base-button class="d-none d-sm-inline-block" type="primary" size="sm" @click="bulk.show = true">
                  <i class="tim-icons icon-bullet-list-67 mr-1"></i> Bulk actions
                </base-button>
                <base-button class="d-sm-none" type="primary" size="sm" icon title="Bulk actions" @click="bulk.show = true">
                  <i class="tim-icons icon-bullet-list-67"></i>
                </base-button>
              </template>
            </template>
          </tab-rail>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            :loading="refreshing"
            saved-filters-key="carts"
            :extra-saved-state="{ tab: activeTabIndex, sort: sort }"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
            @restore-extra="onRestoreExtra"
          >
            <template #actions>
              <base-button v-if="activeTabIndex !== 2" icon class="btn-link text-white" title="Download CSV" @click="downloadCartsCSV"
                           :disabled="(activeTabIndex === 0 ? abandoned_carts.length : active_carts.length) === 0">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>

          <!-- Abandoned carts tab -->
          <data-table
            v-if="activeTabIndex === 0"
            class="mt-3"
            :columns="cartColumns"
            :rows="abandoned_carts"
            row-key="id"
            :loading="refreshing"
            :page="currentPage"
            :per-page="perPage"
            :total="totalRows"
            :sort-value="sort"
            :default-sort="defaultSortFor(activeTabIndex)"
            clickable
            @row-click="openCart"
            @update:sort="onSortChange"
            @update:page="onPageChange"
          >
            <template #cell(customer)="{ row: item }">
              <div class="cart-nowrap">
                <span v-if="item.customer_first_name || item.customer_last_name" class="data-table__primary">{{ item.customer_first_name }} {{ item.customer_last_name }}</span>
                <span v-else class="data-table__muted font-italic">Anonymous customer</span>
              </div>
              <div v-if="item.customer_email" class="data-table__muted cart-contact">
                <i class="tim-icons icon-email-85"></i>
                <span class="cart-contact__value">{{ item.customer_email }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_email)"></i>
              </div>
              <div v-if="item.customer_phone" class="data-table__muted cart-contact">
                <i class="tim-icons icon-mobile"></i>
                <span class="cart-contact__value">{{ formatPhone(item.country, item.customer_phone) }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_phone)"></i>
              </div>
            </template>
            <template #cell(errors)="{ row: item }">
              <div v-if="unresolvedErrorTypes(item).length > 0" class="data-table__chips">
                <span v-for="error in unresolvedErrorTypes(item)" :key="'ERR' + error.error_type" class="badge-tag badge-tag--danger" :title="error.error_message">{{ capitalizeFirstLetter(error.error_type) }}</span>
              </div>
              <span v-else class="data-table__muted">–</span>
            </template>
            <template #cell(step)="{ row: item }"><span class="cart-nowrap">{{ item.event_name }}</span></template>
            <template #cell(latest)="{ row: item }">
              <span class="data-table__muted cart-nowrap">{{ getLocalDate(latestOf(item)) }}, {{ getLocalTime(latestOf(item)) }}</span>
            </template>
            <template #cell(items)="{ row: item }">
              <div class="cart-items">
                <template v-if="cartLines(item).first">
                  <span class="data-table__primary">{{ cartLines(item).first.name }}</span>
                  <span v-if="cartLines(item).first.meta" class="data-table__muted"> {{ cartLines(item).first.meta }}</span>
                  <span v-if="cartLines(item).rest" class="cart-items__rest"> · {{ cartLines(item).rest }}</span>
                  <span v-if="cartLines(item).extra > 0" class="data-table__muted"> +{{ cartLines(item).extra }}</span>
                </template>
                <span v-else class="data-table__muted font-italic">No items</span>
              </div>
            </template>
            <template #cell(total)="{ row: item }">
              <span class="cart-total">{{ getAmount(item.cart_value) }}</span>
            </template>
            <template #cell(email_status)="{ row: item }">
              <span v-if="item.email_sent" class="badge-tag badge-tag--primary">Sent</span>
              <span v-else class="badge-tag badge-tag--danger">Not sent</span>
            </template>
            <template #actions="{ row: item }">
              <el-dropdown>
                <base-button simple type="primary" link size="sm"><i class="tim-icons el-icon-more"></i></base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1" href="#" @click.prevent="openCart(item)">
                      <i class="tim-icons icon-zoom-split mr-2"></i> View cart
                    </a>
                    <a v-if="item.customer_email" class="dropdown-item mt-1" href="#" @click.prevent="sendEmail(item)">
                      <i style="color: #2dce89" class="tim-icons icon-email-85 mr-2"></i> Send the abandoned cart email
                    </a>
                    <hr>
                    <a class="dropdown-item" href="#" @click.prevent="deleteCartPrompt(item)">
                      <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Delete permanently
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
            <template #empty>
              <img src="/img/pandas/empty-transactions.png" alt="" class="empty-state-img empty-state-img--md" />
              <h4 class="mt-3 empty-state-text">{{ hasActiveFilters ? 'No results match these filters' : 'No abandoned carts' }}</h4>
              <p class="data-table__muted">{{ hasActiveFilters ? 'Try a different customer, event, item, date or value.' : 'Carts customers leave before paying show up here so you can follow up.' }}</p>
            </template>
          </data-table>

          <!-- Active carts tab -->
          <data-table
            v-if="activeTabIndex === 1"
            class="mt-3"
            :columns="activeColumns"
            :rows="active_carts"
            row-key="id"
            :loading="refreshing"
            :page="currentPage"
            :per-page="perPage"
            :total="totalRows"
            :sort-value="sort"
            :default-sort="defaultSortFor(activeTabIndex)"
            clickable
            @row-click="openCart"
            @update:sort="onSortChange"
            @update:page="onPageChange"
          >
            <template #cell(customer)="{ row: item }">
              <div class="cart-nowrap">
                <span v-if="item.customer_first_name || item.customer_last_name" class="data-table__primary">{{ item.customer_first_name }} {{ item.customer_last_name }}</span>
                <span v-else class="data-table__muted font-italic">Anonymous customer</span>
              </div>
              <div v-if="item.customer_email" class="data-table__muted cart-contact">
                <i class="tim-icons icon-email-85"></i>
                <span class="cart-contact__value">{{ item.customer_email }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_email)"></i>
              </div>
              <div v-if="item.customer_phone" class="data-table__muted cart-contact">
                <i class="tim-icons icon-mobile"></i>
                <span class="cart-contact__value">{{ formatPhone(item.country, item.customer_phone) }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_phone)"></i>
              </div>
            </template>
            <template #cell(errors)="{ row: item }">
              <div v-if="unresolvedErrorTypes(item).length > 0" class="data-table__chips">
                <span v-for="error in unresolvedErrorTypes(item)" :key="'ERR' + error.error_type" class="badge-tag badge-tag--danger" :title="error.error_message">{{ capitalizeFirstLetter(error.error_type) }}</span>
              </div>
              <span v-else class="data-table__muted">–</span>
            </template>
            <template #cell(step)="{ row: item }"><span class="cart-nowrap">{{ item.event_name }}</span></template>
            <template #cell(latest)="{ row: item }">
              <span class="data-table__muted cart-nowrap">{{ getLocalDate(latestOf(item)) }}, {{ getLocalTime(latestOf(item)) }}</span>
            </template>
            <template #cell(items)="{ row: item }">
              <div class="cart-items">
                <template v-if="cartLines(item).first">
                  <span class="data-table__primary">{{ cartLines(item).first.name }}</span>
                  <span v-if="cartLines(item).first.meta" class="data-table__muted"> {{ cartLines(item).first.meta }}</span>
                  <span v-if="cartLines(item).rest" class="cart-items__rest"> · {{ cartLines(item).rest }}</span>
                  <span v-if="cartLines(item).extra > 0" class="data-table__muted"> +{{ cartLines(item).extra }}</span>
                </template>
                <span v-else class="data-table__muted font-italic">No items</span>
              </div>
            </template>
            <template #cell(total)="{ row: item }">
              <span class="cart-total">{{ getAmount(item.cart_value) }}</span>
            </template>
            <template #actions="{ row: item }">
              <el-dropdown>
                <base-button simple type="primary" link size="sm"><i class="tim-icons el-icon-more"></i></base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1" href="#" @click.prevent="openCart(item)">
                      <i class="tim-icons icon-zoom-split mr-2"></i> View cart
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
            <template #empty>
              <img src="/img/pandas/empty-transactions.png" alt="" class="empty-state-img empty-state-img--md" />
              <h4 class="mt-3 empty-state-text">{{ hasActiveFilters ? 'No results match these filters' : 'No active carts' }}</h4>
              <p class="data-table__muted">{{ hasActiveFilters ? 'Try a different customer, event, item, date or value.' : 'Customers currently shopping on your booking site appear here.' }}</p>
            </template>
          </data-table>

          <!-- Cart errors tab -->
          <data-table
            v-if="activeTabIndex === 2"
            class="mt-3 error-table"
            :columns="errorColumns"
            :rows="cart_errors"
            row-key="id"
            :loading="refreshing"
            :page="currentPage"
            :per-page="perPage"
            :total="totalRows"
            :sort-value="sort"
            :default-sort="defaultSortFor(activeTabIndex)"
            clickable
            :row-class="errorRowClass"
            @row-click="openCart"
            @update:sort="onSortChange"
            @update:page="onPageChange"
          >
            <template #cell(customer)="{ row: item }">
              <div class="cart-nowrap">
                <span v-if="item.customer_first_name || item.customer_last_name" class="data-table__primary">{{ item.customer_first_name }} {{ item.customer_last_name }}</span>
                <span v-else class="data-table__muted font-italic">Anonymous customer</span>
              </div>
              <div v-if="item.customer_email" class="data-table__muted cart-contact">
                <i class="tim-icons icon-email-85"></i>
                <span class="cart-contact__value">{{ item.customer_email }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_email)"></i>
              </div>
              <div v-if="item.customer_phone" class="data-table__muted cart-contact">
                <i class="tim-icons icon-mobile"></i>
                <span class="cart-contact__value">{{ formatPhone(item.country, item.customer_phone) }}</span>
                <i class="tim-icons el-icon-copy-document error-copy-btn" @click.stop="copyToClipboard(item.customer_phone)"></i>
              </div>
            </template>
            <template #cell(error_summary)="{ row: item }">
              <div class="d-flex align-items-center mb-1 cart-nowrap">
                <small class="text-white text-uppercase" style="font-weight: 600; letter-spacing: 0.3px;">{{ capitalizeFirstLetter(item.error_type) }}</small>
                <small class="ml-2 error-secondary-text"> · {{ getLocalDate(item.timestamp) }}, {{ getLocalTime(item.timestamp) }}</small>
              </div>
              <div class="error-message-code">
                {{ item.error_message }}
                <span v-if="item.error_code" class="text-muted"> · {{ item.error_code }}</span>
              </div>
              <div v-if="getPromoCodeFromError(item)" class="mt-1">
                <small><i class="tim-icons icon-tag mr-1" style="color: #e14eca;"></i><code style="color: #e14eca;">{{ getPromoCodeFromError(item) }}</code></small>
              </div>
            </template>
            <template #cell(latest)="{ row: item }">
              <span class="data-table__muted cart-nowrap">{{ getLocalDate(latestOf(item)) }}, {{ getLocalTime(latestOf(item)) }}</span>
            </template>
            <template #cell(items)="{ row: item }">
              <div class="cart-items">
                <template v-if="cartLines(item).first">
                  <span class="data-table__primary">{{ cartLines(item).first.name }}</span>
                  <span v-if="cartLines(item).first.meta" class="data-table__muted"> {{ cartLines(item).first.meta }}</span>
                  <span v-if="cartLines(item).rest" class="cart-items__rest"> · {{ cartLines(item).rest }}</span>
                  <span v-if="cartLines(item).extra > 0" class="data-table__muted"> +{{ cartLines(item).extra }}</span>
                </template>
                <span v-else class="data-table__muted font-italic">No items</span>
              </div>
            </template>
            <template #cell(total)="{ row: item }">
              <span class="cart-total">{{ getAmount(item.cart_value) }}</span>
            </template>
            <template #actions="{ row: item }">
              <el-dropdown>
                <base-button simple type="primary" link size="sm"><i class="tim-icons el-icon-more"></i></base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1" href="#" @click.prevent="openCart(item)">
                      <i class="tim-icons icon-zoom-split mr-2"></i> View cart
                    </a>
                    <a v-if="item.customer_email" class="dropdown-item mt-1" href="#" @click.prevent="sendEmail(item)">
                      <i style="color: #2dce89" class="tim-icons icon-email-85 mr-2"></i> Send the abandoned cart email
                    </a>
                    <a class="dropdown-item mt-1" href="#" @click.prevent="toggleErrorResolution(item)">
                      <i class="tim-icons icon-check-2 mr-2" style="color: #2dce89"></i> Mark as resolved
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
            <template #empty>
              <img src="/img/pandas/empty-transactions.png" alt="" class="empty-state-img empty-state-img--md" />
              <h4 class="mt-3 empty-state-text">{{ hasActiveFilters ? 'No results match these filters' : 'No cart errors' }}</h4>
              <p class="data-table__muted">{{ hasActiveFilters ? 'Try a different customer, event, item, date or value.' : 'Errors customers hit while checking out are listed here for triage.' }}</p>
            </template>
          </data-table>

          <!-- Cart Analytics Tab -->
          <div v-if="activeTabIndex === 3">
            <!-- Summary Cards Row -->
            <div class="row mb-4" v-if="analytics.stage_analytics && analytics.error_analytics">
              <div class="col-lg-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-5">
                        <div class="info-icon text-center icon-warning">
                          <i class="tim-icons icon-alert-circle-exc"></i>
                        </div>
                      </div>
                      <div class="col-7">
                        <div class="numbers">
                          <p class="card-category">Active Errors</p>
                          <h3 class="card-title">{{ getTotalActiveErrors() }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-5">
                        <div class="info-icon text-center icon-danger">
                          <i class="tim-icons icon-money-coins"></i>
                        </div>
                      </div>
                      <div class="col-7">
                        <div class="numbers">
                          <p class="card-category">Lost Revenue</p>
                          <h3 class="card-title">{{ getTotalLostRevenue() }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-5">
                        <div class="info-icon text-center icon-info">
                          <i class="tim-icons icon-cart"></i>
                        </div>
                      </div>
                      <div class="col-7">
                        <div class="numbers">
                          <p class="card-category">Affected Carts</p>
                          <h3 class="card-title">{{ getTotalAffectedCarts() }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-5">
                        <div class="info-icon text-center icon-success">
                          <i class="tim-icons icon-chart-bar-32"></i>
                        </div>
                      </div>
                      <div class="col-7">
                        <div class="numbers">
                          <p class="card-category">Avg Cart Value</p>
                          <h3 class="card-title">{{ getAvgCartValue() }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Analytics Content -->
            <div class="row">
              <!-- Error Analytics Chart -->
              <div class="col-lg-6 col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h4 class="card-title">Top Cart Errors (Last 30 Days)</h4>
                    <p class="card-category">Unresolved errors affecting customer experience</p>
                  </div>
                  <div class="card-body">
                    <div v-if="analytics.error_analytics && analytics.error_analytics.length > 0">
                      <div v-for="(error, index) in analytics.error_analytics" :key="'error_' + index"
                           class="d-flex justify-content-between align-items-center mb-3 p-3"
                           style="background-color: rgba(255,255,255,0.05); border-radius: 8px;">
                        <div class="flex-grow-1">
                          <div class="d-flex align-items-center mb-1">
                            <badge :type="getErrorBadgeType(error.error_type)" class="mr-2">
                              {{ capitalizeFirstLetter(error.error_type) }}
                            </badge>
                            <span class="text-white font-weight-bold">{{ error.error_count }} errors</span>
                          </div>
                          <div class="d-flex align-items-center">
                            <small class="text-muted mr-3">
                              <i class="tim-icons icon-single-02 mr-1"></i>
                              {{ error.affected_carts }} carts affected
                            </small>
                            <small class="text-muted">
                              <i class="tim-icons icon-money-coins mr-1"></i>
                              {{ getAmount(error.total_lost_value || 0) }} lost revenue
                            </small>
                          </div>
                        </div>
                        <div class="ml-3">
                          <div class="progress" style="width: 60px; height: 8px;">
                            <div class="progress-bar"
                                 :class="'bg-' + getErrorBadgeType(error.error_type)"
                                 :style="{width: getErrorPercentage(error.error_count, index) + '%'}">
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mt-3 pt-3" style="border-top: 1px solid rgba(255,255,255,0.1);">
                        <base-button @click.native="viewAllErrors" type="info" size="sm">
                          <i class="tim-icons icon-bullet-list-67 mr-1"></i>
                          View All Errors
                        </base-button>
                      </div>
                    </div>

                    <div v-else class="text-center py-4">
                      <i class="tim-icons icon-check-2" style="font-size: 3em; color: #2dce89;"></i>
                      <h5 class="mt-3 text-success">No Active Errors</h5>
                      <p class="text-muted">Great job! No unresolved cart errors in the last 30 days.</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Cart Stage Analytics -->
              <div class="col-lg-6 col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h4 class="card-title">Cart Abandonment by Stage</h4>
                    <p class="card-category">Where customers are dropping off</p>
                  </div>
                  <div class="card-body">
                    <div v-if="analytics.stage_analytics && analytics.stage_analytics.length > 0">
                      <div v-for="(stage, index) in analytics.stage_analytics.slice(0, 6)"
                           :key="'stage_' + index"
                           class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <span class="text-white">{{ stage.stage_name }}</span>
                          <div class="text-right">
                            <span class="text-white font-weight-bold">{{ stage.cart_count }}</span>
                            <small class="text-muted ml-2">{{ getAmount(stage.total_value) }}</small>
                          </div>
                        </div>
                        <div class="progress mb-1" style="height: 8px;">
                          <div class="progress-bar bg-primary"
                               :style="{width: getStagePercentage(stage.cart_count, index) + '%'}">
                          </div>
                        </div>
                        <div class="d-flex justify-content-between">
                          <small class="text-muted">
                            Avg: {{ getAmount(stage.avg_value || 0) }}
                          </small>
                          <small class="text-muted">
                            {{ getStagePercentage(stage.cart_count, index).toFixed(1) }}%
                          </small>
                        </div>
                      </div>
                    </div>

                    <div v-else class="text-center py-4">
                      <i class="tim-icons icon-chart-pie-36" style="font-size: 3em; color: #1d8cf8;"></i>
                      <h5 class="mt-3">No Abandonment Data</h5>
                      <p class="text-muted">No abandoned carts found in the selected time period.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </card>
      </div>
    </div>

    <base-alert-dialog
      :show="alertDialog.show"
      :title="alertDialog.title"
      :message="alertDialog.message"
      :variant="alertDialog.variant"
      :show-cancel="alertDialog.showCancel"
      :confirm-text="alertDialog.confirmText"
      :confirm-variant="alertDialog.confirmVariant"
      @confirm="onAlertConfirm"
      @cancel="alertDialog.show = false"
    />

    <cart-bulk-action-drawer
      :show.sync="bulk.show"
      :tab="activeTabIndex"
      @close="bulk.show = false"
      @done="onBulkDone"
    />

    <cart-drawer
      :show.sync="cartDrawer.show"
      :cart="cartDrawer.cart"
      :tab="activeTabIndex"
      :format-amount="getAmount"
      :format-date="getFormattedDate"
      :format-time="getTime"
      :format-date-time="getLocalDateTime"
      @close="cartDrawer.show = false"
      @delete="onDrawerDelete"
      @send-email="onDrawerSendEmail"
    />
  </div>
</template>

<script>
import Badge from "@/components/Badge";
import {Dropdown, DropdownItem, DropdownMenu, Select, Option} from "element-ui";
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import currencies from "@/assets/currencies.json";
import _ from "lodash";
import {formatPhoneDisplay} from "@/util/phoneFormatter";
import CartBulkActionDrawer from "@/pages/Purchases/CartBulkActionDrawer.vue";
import TabRail from "@/components/TabRail.vue";
import CartDrawer from "@/pages/Purchases/CartDrawer.vue";

export default {
  name: "ActiveCarts",
  components: {
    Badge,
    CartBulkActionDrawer,
    TabRail,
    CartDrawer,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option
  },
  data() {
    return {
      enable_utm_source_tracking: 0,
      loading: false,
      refreshing: false,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      group_country: JSON.parse(localStorage.getItem('group')).country || 'US',
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      active_carts: [],
      abandoned_carts: [],
      cart_errors: [],
      analytics: [],
      activeTabIndex: 0,
      currentPage: 1,
      perPageOptions: [5, 10, 25, 50],
      perPage: 10,
      totalRows: 0,
      errorCounts: {
        abandoned: 0,
        active: 0,
        total: 0
      },
      cartDrawer: { show: false, cart: null },
      eventOptions: [],
      itemOptions: [],
      stepOptions: [],
      bulk: { show: false },
      sort: this.buildInitialSort(),
      alertDialog: { show: false, title: '', message: '', variant: 'info', showCancel: false, confirmText: 'OK', confirmVariant: 'primary', onConfirm: null },
      // Flat filter values for the shared <filter-bar>, seeded from the URL.
      filterValues: this.buildInitialFilterValues(),
      // FilterBar (saved views enabled) drives the first fetch via its initial change.
      didInitialFetch: false,
      detailedErrorFields: [
        { key: 'error_type', label: 'Error Type' },
        { key: 'error_count', label: 'Count' },
        { key: 'affected_carts', label: 'Affected Carts' },
        { key: 'total_lost_value', label: 'Lost Revenue' },
        { key: 'avg_cart_value', label: 'Avg Cart Value' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },
  computed: {
    tabs() {
      return [
        { name: 'Abandoned carts', shortName: 'Abandoned' },
        { name: 'Active carts', shortName: 'Active' },
        { name: 'Cart errors', shortName: 'Errors' }
      ];
    },
    filterSchema() {
      const schema = [
        { key: 'customer', type: 'text', label: 'Customer', placeholder: 'Customer name or email', operatorLabel: 'contains', primary: true },
        { key: 'events', type: 'multiselect', label: 'Events', primary: true, options: this.eventOptions },
        { key: 'items', type: 'multiselect', label: 'Items', primary: true, options: this.itemOptions },
        { key: 'step', type: 'multiselect', label: 'Step', primary: true, options: this.stepOptions },
        { key: 'error_type', type: 'multiselect', label: 'Errors', primary: true, selectAll: true, selectAllLabel: 'Any error', options: this.errorTypeOptions },
        { key: 'date', type: 'daterange', label: this.activeTabIndex === 2 ? 'Error date' : 'Cart date', primary: true },
        { key: 'cart_value', type: 'numberrange', label: 'Cart value', primary: true }
      ];
      if (this.activeTabIndex === 0) {
        schema.push({
          key: 'email_sent', type: 'select', label: 'Email', primary: true,
          options: [{ label: 'Sent', value: '1' }, { label: 'Not sent', value: '0' }]
        });
      }
      return schema;
    },
    cartColumns() {
      return [
        { key: 'customer', label: 'Customer', sortable: true, sortKey: 'customer' },
        { key: 'step', label: 'Step', width: '1%' },
        { key: 'latest', label: 'Latest activity', sortable: true, sortKey: 'last_updated', width: '1%' },
        { key: 'items', label: 'Items' },
        { key: 'total', label: 'Total', sortable: true, sortKey: 'cart_value', align: 'right', width: '1%' },
        { key: 'errors', label: 'Errors', sortable: true, sortKey: 'errors', width: '1%' },
        { key: 'email_status', label: 'Email', sortable: true, sortKey: 'email', width: '1%' }
      ];
    },
    activeColumns() {
      return this.cartColumns.filter(c => c.key !== 'email_status');
    },
    errorColumns() {
      return [
        { key: 'customer', label: 'Customer', sortable: true, sortKey: 'customer', width: '1%' },
        { key: 'error_summary', label: 'Error', sortable: true, sortKey: 'error_date' },
        { key: 'items', label: 'Items', width: '1%' },
        { key: 'total', label: 'Total', sortable: true, sortKey: 'cart_value', align: 'right', width: '1%' }
      ];
    },
    hasActiveFilters() {
      const f = this.filterValues;
      const d = f.date || {};
      const v = f.cart_value || {};
      return !!(f.customer || f.events.length || f.items.length || f.step.length || f.email_sent !== '' || (d.mode === 'days' && d.days) || d.from || d.to || v.from || v.to || (Array.isArray(f.error_type) && f.error_type.length > 0));
    },
    sortOptions() {
      if (this.activeTabIndex === 2) {
        return [
          { key: 'error_date', label: 'Error date' },
          { key: 'cart_value', label: 'Cart value' },
          { key: 'customer', label: 'Customer' }
        ];
      }
      const options = [
        { key: 'last_updated', label: 'Latest activity' },
        { key: 'first_visited', label: 'First visited' },
        { key: 'cart_value', label: 'Cart value' },
        { key: 'customer', label: 'Customer' },
        { key: 'errors', label: 'Unresolved errors' },
        { key: 'email', label: 'Email status' }
      ];
      return this.activeTabIndex === 1 ? options.filter(o => o.key !== 'email') : options;
    },
    errorTypeOptions() {
      return [
        { value: 'resource', label: 'Resource' },
        { value: 'payment', label: 'Payment' },
        { value: 'validation', label: 'Validation' },
        { value: 'promo_code', label: 'Promo code' },
        { value: 'gift_card', label: 'Gift card' },
        { value: 'customer', label: 'Customer' },
        { value: 'add_on', label: 'Add on' },
        { value: 'booking_slot', label: 'Booking slot' },
        { value: 'generic', label: 'Generic' }
      ];
    },
  },
  methods: {
    buildInitialFilterValues() {
      const q = this.$route.query || {};
      const ids = v => v ? String(v).split(',').map(Number).filter(n => n > 0) : [];
      return {
        customer: q.customer || '',
        events: ids(q.event_ids),
        items: ids(q.item_ids),
        date: { mode: 'range', days: '', from: q.from || '', to: q.to || '' },
        cart_value: { from: q.value_from || '', to: q.value_to || '' },
        step: ids(q.step_ids),
        email_sent: q.email_sent === '1' || q.email_sent === '0' ? q.email_sent : '',
        error_type: q.error_types ? String(q.error_types).split(',') : []
      };
    },
    // Plain yyyy-MM-dd from/to for the query string, whichever mode the chip is in.
    computeDateRange() {
      const d = this.filterValues.date || {};
      let from = '';
      let to = '';
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        const today = new Date();
        const start = new Date();
        start.setDate(start.getDate() - Number(d.days));
        const iso = x => x.getFullYear() + '-' + ('0' + (x.getMonth() + 1)).slice(-2) + '-' + ('0' + x.getDate()).slice(-2);
        from = iso(start);
        to = iso(today);
      } else if (d.mode === 'range') {
        from = d.from || '';
        to = d.to || '';
      }
      return { from, to };
    },
    filterQueryString() {
      const parts = [];
      const range = this.computeDateRange();
      if (this.filterValues.customer) parts.push('customer=' + encodeURIComponent(this.filterValues.customer));
      if (range.from) parts.push('from=' + range.from);
      if (range.to) parts.push('to=' + range.to);
      if (Array.isArray(this.filterValues.error_type) && this.filterValues.error_type.length > 0) {
        parts.push('error_types=' + this.filterValues.error_type.join(','));
      }
      if (this.filterValues.step.length > 0) parts.push('step_ids=' + this.filterValues.step.join(','));
      if (this.activeTabIndex === 0 && this.filterValues.email_sent !== '') parts.push('email_sent=' + this.filterValues.email_sent);
      if (this.filterValues.events.length > 0) parts.push('event_ids=' + this.filterValues.events.join(','));
      if (this.filterValues.items.length > 0) parts.push('item_ids=' + this.filterValues.items.join(','));
      const v = this.filterValues.cart_value || {};
      if (v.from !== '' && v.from != null) parts.push('value_from=' + encodeURIComponent(v.from));
      if (v.to !== '' && v.to != null) parts.push('value_to=' + encodeURIComponent(v.to));
      parts.push('sort=' + this.sort.key + '&dir=' + this.sort.dir);
      return '&' + parts.join('&');
    },
    buildInitialSort() {
      const q = this.$route.query || {};
      const dir = q.dir === 'asc' ? 'asc' : 'desc';
      return { key: q.sort || (Number(q.tab) === 2 ? 'error_date' : 'last_updated'), dir };
    },
    defaultSortFor(tab) {
      return { key: tab === 2 ? 'error_date' : 'last_updated', dir: 'desc' };
    },
    onSortChange(value) {
      this.sort = { key: value.key, dir: value.dir === 'asc' ? 'asc' : 'desc' };
      this.currentPage = 1;
      if (this.didInitialFetch) this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    onRestoreExtra(extra) {
      // A saved view carries the tab it was made on; land there before its filters apply.
      if (extra && typeof extra.tab === 'number' && extra.tab !== this.activeTabIndex) {
        this.activeTabIndex = extra.tab;
        this.currentPage = 1;
      }
      if (extra && extra.sort && extra.sort.key) {
        this.sort = { key: extra.sort.key, dir: extra.sort.dir === 'asc' ? 'asc' : 'desc' };
      }
      if (!this.sortOptions.some(o => o.key === this.sort.key)) this.sort = this.defaultSortFor(this.activeTabIndex);
    },
    onBulkDone() {
      this.currentPage = 1;
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    onFiltersChanged(values) {
      this.filterValues = {
        customer: values.customer || '',
        events: Array.isArray(values.events) ? [...values.events] : [],
        items: Array.isArray(values.items) ? [...values.items] : [],
        date: { ...(values.date || { mode: 'range', days: '', from: '', to: '' }) },
        cart_value: { ...(values.cart_value || { from: '', to: '' }) },
        step: Array.isArray(values.step) ? [...values.step] : [],
        email_sent: values.email_sent == null ? '' : String(values.email_sent),
        error_type: Array.isArray(values.error_type) ? [...values.error_type] : []
      };
      if (!this.didInitialFetch) {
        this.didInitialFetch = true;
      } else {
        this.currentPage = 1;
      }
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    showAlert(options) {
      this.alertDialog = {
        show: true,
        title: options.title || '',
        message: options.message || '',
        variant: options.variant || 'info',
        showCancel: !!options.showCancel,
        confirmText: options.confirmText || 'OK',
        confirmVariant: options.confirmVariant || 'primary',
        onConfirm: options.onConfirm || null
      };
    },
    onAlertConfirm() {
      const cb = this.alertDialog.onConfirm;
      this.alertDialog.show = false;
      if (typeof cb === 'function') cb();
    },
    formatPhone(country, phone) {
      // Cart rows carry no country; the group's country is the parsing hint.
      return formatPhoneDisplay(phone, country || this.group_country || 'US');
    },
    getTotalActiveErrors() {
      if (!this.analytics.error_analytics) return 0;
      return this.analytics.error_analytics.reduce((sum, error) => sum + error.error_count, 0);
    },

    getTotalLostRevenue() {
      if (!this.analytics.error_analytics) return this.getAmount(0);
      const total = this.analytics.error_analytics.reduce((sum, error) => sum + (error.total_lost_value || 0), 0);
      return this.getAmount(total);
    },

    getTotalAffectedCarts() {
      if (!this.analytics.error_analytics) return 0;
      return this.analytics.error_analytics.reduce((sum, error) => sum + error.affected_carts, 0);
    },

    getAvgCartValue() {
      if (!this.analytics.stage_analytics || this.analytics.stage_analytics.length === 0) return this.getAmount(0);
      const totalValue = this.analytics.stage_analytics.reduce((sum, stage) => sum + (stage.total_value || 0), 0);
      const totalCarts = this.analytics.stage_analytics.reduce((sum, stage) => sum + stage.cart_count, 0);
      return this.getAmount(totalCarts > 0 ? totalValue / totalCarts : 0);
    },

    getErrorPercentage(errorCount, index) {
      if (!this.analytics.error_analytics || this.analytics.error_analytics.length === 0) return 0;
      const maxErrors = Math.max(...this.analytics.error_analytics.map(e => e.error_count));
      return maxErrors > 0 ? (errorCount / maxErrors) * 100 : 0;
    },

    getStagePercentage(stageCount, index) {
      if (!this.analytics.stage_analytics || this.analytics.stage_analytics.length === 0) return 0;
      const maxStage = Math.max(...this.analytics.stage_analytics.map(s => s.cart_count));
      return maxStage > 0 ? (stageCount / maxStage) * 100 : 0;
    },

    viewAllErrors() {
      this.switchTab(2); // Switch to cart errors tab
    },

    viewErrorDetails(errorType) {
      // Switch to errors tab with filter
      this.switchTab(2);
      this.filterValues = { ...this.filterValues, error_type: [errorType] };
      this.currentPage = 1;
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },

    refreshAnalytics() {
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },

    exportAnalytics() {
      // Implement CSV export of analytics data
      const csvData = this.generateAnalyticsCSV();
      this.downloadCSV(csvData, 'cart_analytics.csv');
    },

    generateAnalyticsCSV() {
      let csv = 'Error Type,Error Count,Affected Carts,Lost Revenue\n';
      if (this.analytics.error_analytics) {
        this.analytics.error_analytics.forEach(error => {
          csv += `${error.error_type},${error.error_count},${error.affected_carts},${error.total_lost_value || 0}\n`;
        });
      }
      return csv;
    },

    downloadCSV(csvData, filename) {
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    async downloadCartsCSV() {
      this.loading = true;
      const label = this.activeTabIndex === 0 ? 'abandoned_carts' : 'active_carts';
      const carts = await this.getAllCarts();
      this.loading = false;

      if (!carts || carts.length === 0) return;

      let headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Cart Value', 'Last Activity', 'Time First Visited', 'Latest Activity', 'Items'];
      if (this.activeTabIndex === 0) {
        headers.push('Email Sent');
      }
      if (this.enable_utm_source_tracking) {
        headers.push('UTM Source', 'UTM Medium', 'UTM Campaign', 'UTM Referrer');
      }

      let csv = headers.join(',') + '\n';

      carts.forEach(cart => {
        const items = [];
        if (cart.bookings_in_cart && cart.bookings_in_cart.length > 0) {
          cart.bookings_in_cart.forEach(b => items.push(b.game_name));
        }
        if (cart.items_in_cart && cart.items_in_cart.length > 0) {
          cart.items_in_cart.forEach(i => items.push(`${i.name} x${i.quantity}`));
        }
        if (cart.gift_cards_in_cart && cart.gift_cards_in_cart.length > 0) {
          cart.gift_cards_in_cart.forEach(gc => items.push(`${gc.name} x${gc.quantity}`));
        }

        let row = [
          this.escapeCsvField(cart.customer_first_name || ''),
          this.escapeCsvField(cart.customer_last_name || ''),
          this.escapeCsvField(cart.customer_email || ''),
          this.escapeCsvField(cart.customer_phone || ''),
          cart.cart_value || 0,
          this.escapeCsvField(cart.event_name || ''),
          this.escapeCsvField(this.formatCsvDateTime(cart.timestamp)),
          this.escapeCsvField(this.formatCsvDateTime(cart.last_updated)),
          this.escapeCsvField(items.join('; '))
        ];

        if (this.activeTabIndex === 0) {
          row.push(cart.email_sent ? 'Yes' : 'No');
        }
        if (this.enable_utm_source_tracking) {
          row.push(
            this.escapeCsvField(cart.utm_source || ''),
            this.escapeCsvField(cart.utm_medium || ''),
            this.escapeCsvField(cart.utm_campaign || ''),
            this.escapeCsvField(cart.utm_referrer || '')
          );
        }

        csv += row.join(',') + '\n';
      });

      this.downloadCSV(csv, `${label}_${new Date().toISOString().slice(0, 10)}.csv`);
    },
    escapeCsvField(value) {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    },
    capitalizeFirstLetter(string) {
      if (!string) {
        return string;
      }
      string = string.replace(/_/g, ' ');
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getErrorBadgeType(errorType) {
      const errorTypeMap = {
        'resource_management': 'warning',
        'promo_code': 'info',
        'payment': 'danger',
        'validation': 'secondary',
        'system': 'danger'
      };
      return errorTypeMap[errorType] || 'danger';
    },
    getPromoCodeFromError(error) {
      if (!error || !error.error_data) return null;
      try {
        const errorData = typeof error.error_data === 'string'
          ? JSON.parse(error.error_data)
          : error.error_data;
        return errorData.promoCode || null;
      } catch (e) {
        return null;
      }
    },
    // One chip per unresolved error type on a cart row; the message of the latest one is the tooltip.
    unresolvedErrorTypes(item) {
      const seen = new Set();
      return (item.errors || []).filter(e => {
        if (e.resolved || seen.has(e.error_type)) return false;
        seen.add(e.error_type);
        return true;
      });
    },
    uniqueErrors(errors) {
      if (!errors) return [];
      const seen = new Set();
      return errors.filter(e => {
        const key = e.error_type + '|' + e.error_message;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.$notify({
        type: 'success',
        message: 'Copied to clipboard',
        icon: 'tim-icons icon-check-2'
      });
    },
    toggleErrorRowDetails(item) {
      this.$set(item, '_showDetails', !item._showDetails);
    },
    errorRowClass(item) {
      return item && item.resolved ? 'error-row-resolved' : '';
    },
    async toggleErrorResolution(error) {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/carts/${error.cart_id}/error`;

        const data = {
          resolved: !error.resolved
        };

        await axios.put(url, data, config);

        this.loading = false;
        this.$notify({ type: 'success', message: `Error ${error.resolved ? 'marked as unresolved' : 'marked as resolved'}`, timeout: 4000 });

        // Refresh the current tab data
        this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
      } catch (err) {
        this.loading = false;
        console.log(err);
        this.handleApiError(err);
      }
    },
    openCart(cart) {
      this.cartDrawer.cart = cart;
      this.cartDrawer.show = true;
    },
    onDrawerDelete(cart) {
      this.cartDrawer.show = false;
      this.deleteCartPrompt(cart);
    },
    onDrawerSendEmail(cart) {
      this.cartDrawer.show = false;
      this.sendEmail(cart);
    },
    latestOf(item) {
      // Errors-tab rows carry the error timestamp; cart rows their latest activity.
      if (this.activeTabIndex === 2) return item.timestamp;
      return item.last_updated && !String(item.last_updated).includes('0000') ? item.last_updated : item.timestamp;
    },
    getLocalDateTime(d) {
      return `${this.getLocalDate(d)}, ${this.getLocalTime(d)}`;
    },
    // One-line summary of a cart: the first booking (or item) named, the rest listed, overflow counted.
    cartLines(item) {
      const bookings = item.bookings_in_cart || [];
      const items = item.items_in_cart || [];
      const gifts = item.gift_cards_in_cart || [];
      const lines = [
        ...bookings.map(b => ({ name: b.game_name, meta: `${this.getFormattedDate(b.booking_date)}, ${this.getTime(b.start_time)}` })),
        ...items.map(i => ({ name: i.name, meta: '', qty: Number(i.quantity) || 1 })),
        ...gifts.map(g => ({ name: g.name, meta: '', qty: Number(g.quantity) || 1 }))
      ];
      if (lines.length === 0) return { first: null, rest: '', extra: 0 };
      const label = l => l.qty && l.qty > 1 ? `${l.name} ×${l.qty}` : l.name;
      const MAX_REST = 2;
      const rest = lines.slice(1, 1 + MAX_REST).map(label).join(' · ');
      return { first: lines[0], rest, extra: Math.max(0, lines.length - 1 - MAX_REST) };
    },
    async loadFilterOptions() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const games = await axios.get(`${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/games`, config);
        this.eventOptions = (Array.isArray(games.data) ? games.data : []).map(g => ({ label: g.name, value: g.id }));
        const inventory = await axios.get(`${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/inventory?autocomplete=`, config);
        this.itemOptions = (inventory.data.items || []).map(i => ({ label: i.name, value: i.id }));
      } catch (err) {
        console.log(err);
      }
    },
    viewCartDetails(error) {
      // Navigate to specific cart or open cart details
      // This could redirect to another page or open another modal
      console.log('View cart details for:', error.cart_id);
      // Example: this.$router.push(`/carts/${error.cart_id}`);
    },
    updateErrorCounts() {
      // Count errors by cart status
      let abandonedCount = 0;
      let activeCount = 0;

      this.abandoned_carts.forEach(cart => {
        if (cart.errors && cart.errors.length > 0) {
          abandonedCount += cart.errors.filter(error => !error.resolved).length;
        }
      });

      this.active_carts.forEach(cart => {
        if (cart.errors && cart.errors.length > 0) {
          activeCount += cart.errors.filter(error => !error.resolved).length;
        }
      });

      // this.errorCounts = {
      //   abandoned: abandonedCount,
      //   active: activeCount,
      //   total: this.cart_errors.filter(error => !error.resolved).length
      // };
    },
    async sendEmail(item) {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/carts/${item.id || item.cart_id}`;

        const data = item;

        const response = await axios.post(url, data, config);

        this.loading = false;
        this.$notify({ type: 'success', message: 'Cart abandonment email has been sent', timeout: 4000 });
        this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
      } catch (err) {
        this.loading = false;
        console.log(err);
        this.handleApiError(err);
      }
    },
    async deleteCart(item) {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/carts/${item.id}`;

        await axios.delete(url, config);

        this.loading = false;
        this.$notify({ type: 'success', message: 'Cart has been deleted', timeout: 4000 });
        this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
      } catch (err) {
        this.loading = false;
        console.log(err);
        this.handleApiError(err);
      }
    },
    deleteCartPrompt(item) {
      this.showAlert({
        title: 'Delete this cart?',
        message: 'This permanently deletes the cart and cannot be undone.',
        variant: 'danger',
        showCancel: true,
        confirmText: 'Delete cart',
        confirmVariant: 'danger',
        onConfirm: () => this.deleteCart(item)
      });
    },
    async getAllCarts() {
      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      const count = 5000;
      const key = this.activeTabIndex === 0 ? 'abandoned_carts' : 'active_carts';
      let promises = [];
      let csvData = [];
      let offset = 0;

      while (offset <= this.totalRows) {
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/carts?count=${count}&offset=${offset}&type=${this.activeTabIndex}&include_errors=0`;
        promises.push(
          axios.get(url, config)
            .then(response => {
              csvData.push(response.data[key]);
            })
            .catch(err => {
              console.log(err);
              this.handleApiError(err);
            })
        );
        offset += count;
      }

      await Promise.all(promises);

      let arr = [];
      csvData.forEach(element => {
        arr = arr.concat(element);
      });

      // Parse JSON fields
      for (let n in arr) {
        arr[n].bookings_in_cart = JSON.parse(arr[n].bookings_in_cart || '[]');
        arr[n].items_in_cart = JSON.parse(arr[n].items_in_cart || '[]');
        arr[n].gift_cards_in_cart = JSON.parse(arr[n].gift_cards_in_cart || '[]');
      }

      return arr;
    },
    onPerPageChange(n) {
      if (n != null) this.perPage = n;
      this.currentPage = 1;
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    switchTab(index) {
      this.activeTabIndex = index;
      if (!this.sortOptions.some(o => o.key === this.sort.key)) this.sort = this.defaultSortFor(index);
      // for (let n in this.categories) {
      //   this.categories[n].active = false;
      // }
      // this.categories[index].active = true;
      this.currentPage = 1;
      this.getCarts(this.perPage, this.getOffset(), this.activeTabIndex);
    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getLocalTime(d) {
      return formatGroupTime(d);
    },
    // Group date and time formats for CSV cells (no day-of-week prefix).
    formatCsvDateTime(d) {
      if (!d) return '';
      return formatGroupDate(d, { dayOfWeek: false }) + ' ' + formatGroupTime(d);
    },
    getTime(d) {
      return time(this.time_format, d);
    },
    getLocalDate(d) {
      return formatGroupDate(d);
    },
    getDayOfWeek(d) {
      if (JSON.parse(localStorage.getItem('group')).show_day_of_week) {
        return dayOfWeek(d) + ', ';
      }
      return '';
    },
    getFormattedDate(d) {
      if (d instanceof Date) {
        d = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
      }
      return formattedDate(this.date_format, d);
    },
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken);
          })
          .catch(err => console.log(err));
      });
    },
    async getCarts(count, offset, tab) {

      this.refreshing = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const companyId = localStorage.getItem('companyId');
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/carts?count=${count}&offset=${offset}&type=${tab}&include_errors=1${this.filterQueryString()}`;

        const response = await axios.get(url, config);

        this.refreshing = false;
        this.active_carts = response.data.active_carts || [];
        this.abandoned_carts = response.data.abandoned_carts || [];
        this.cart_errors = response.data.cart_errors || [];
        this.analytics = response.data.analytics || [];
        this.totalRows = response.data.total_rows;
        if (Array.isArray(response.data.steps)) this.stepOptions = response.data.steps.map(st => ({ label: st.event_name, value: st.id }));
        this.enable_utm_source_tracking = response.data.enable_utm_source_tracking;



        // Parse cart data
        this.parseCartData();


        // Update error counts
        // this.updateErrorCounts();

        this.addQueryParameters();

      } catch (err) {
        this.refreshing = false;
        console.log(err);
        this.handleApiError(err);
      }
    },
    parseCartData() {
      // Parse JSON data for active carts
      for (let n in this.active_carts) {
        this.active_carts[n].bookings_in_cart = JSON.parse(this.active_carts[n].bookings_in_cart || '[]');
        this.active_carts[n].items_in_cart = JSON.parse(this.active_carts[n].items_in_cart || '[]');
        this.active_carts[n].gift_cards_in_cart = JSON.parse(this.active_carts[n].gift_cards_in_cart || '[]');
        this.active_carts[n].errors = this.active_carts[n].errors || [];
      }

      // Parse JSON data for abandoned carts
      for (let n in this.abandoned_carts) {
        this.abandoned_carts[n].bookings_in_cart = JSON.parse(this.abandoned_carts[n].bookings_in_cart || '[]');
        this.abandoned_carts[n].items_in_cart = JSON.parse(this.abandoned_carts[n].items_in_cart || '[]');
        this.abandoned_carts[n].gift_cards_in_cart = JSON.parse(this.abandoned_carts[n].gift_cards_in_cart || '[]');
        this.abandoned_carts[n].errors = this.abandoned_carts[n].errors || [];
      }

      // Parse JSON data for cart errors
      for (let n in this.cart_errors) {
        this.cart_errors[n].bookings_in_cart = JSON.parse(this.cart_errors[n].bookings_in_cart || '[]');
        this.cart_errors[n].items_in_cart = JSON.parse(this.cart_errors[n].items_in_cart || '[]');
        this.cart_errors[n].gift_cards_in_cart = JSON.parse(this.cart_errors[n].gift_cards_in_cart || '[]');
      }
    },
    handleApiError(err) {
      if (err.response && err.response.status === 401) {
        this.showAlert({
          title: 'No access',
          message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
          variant: 'danger',
          onConfirm: () => this.$router.push('/')
        });
      } else {
        this.showAlert({ title: 'Something went wrong', message: 'Please try again in a moment.', variant: 'danger' });
      }
    },
    addQueryParameters() {
      this.$nextTick(() => {
        const routePage = parseInt(this.$route.query.page) || 1;
        this.currentPage = Math.max(routePage, 1);
      });

      const range = this.computeDateRange();
      const newQuery = {
        count: this.perPage,
        page: this.currentPage,
        tab: this.activeTabIndex
      };
      // Keep the onboarding wizard params so navigateToTask lands and stays on this page.
      for (const k of ['onboarding', 'categoryId', 'taskId']) {
        if (this.$route.query[k]) newQuery[k] = this.$route.query[k];
      }
      if (this.filterValues.customer) newQuery.customer = this.filterValues.customer;
      if (range.from) newQuery.from = range.from;
      if (range.to) newQuery.to = range.to;
      if (this.filterValues.error_type.length > 0) newQuery.error_types = this.filterValues.error_type.join(',');
      if (this.filterValues.step.length > 0) newQuery.step_ids = this.filterValues.step.join(',');
      if (this.activeTabIndex === 0 && this.filterValues.email_sent !== '') newQuery.email_sent = this.filterValues.email_sent;
      if (this.filterValues.events.length > 0) newQuery.event_ids = this.filterValues.events.join(',');
      if (this.filterValues.items.length > 0) newQuery.item_ids = this.filterValues.items.join(',');
      const v = this.filterValues.cart_value || {};
      if (v.from !== '' && v.from != null) newQuery.value_from = v.from;
      if (v.to !== '' && v.to != null) newQuery.value_to = v.to;
      newQuery.sort = this.sort.key;
      newQuery.dir = this.sort.dir;

      this.$router.replace({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    getOffset(){
      if(!this.currentPage || this.currentPage < 1){
        this.currentPage = 1
      }
      return Number((this.currentPage * this.perPage) - this.perPage)
    },
  },
  mounted() {
    this.perPage = Number(this.$route.query.count) || 10;
    this.currentPage = Math.max(Number(this.$route.query.page) || 1, 1)
    this.activeTabIndex = Number(this.$route.query.tab) || 0;
    this.currency_symbol = _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol;
    // The first fetch is triggered by <filter-bar>'s initial change (see onFiltersChanged).
    this.loadFilterOptions();
  }
};
</script>

<style scoped>
.text-underline {
  text-decoration: underline;
}
.card-stats .info-icon {
  width: 66px;
  height: 66px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon.icon-warning {
  background: linear-gradient(60deg, #ffa726, #fb8c00);
}

.info-icon.icon-danger {
  background: linear-gradient(60deg, #ef5350, #e53935);
}

.info-icon.icon-info {
  background: linear-gradient(60deg, #26c6da, #00acc1);
}

.info-icon.icon-success {
  background: linear-gradient(60deg, #66bb6a, #43a047);
}

.progress {
  background-color: rgba(255, 255, 255, 0.1);
}

.card-stats .numbers {
  font-family: "Montserrat", sans-serif;
  text-align: right;
}

.card-stats .card-category {
  color: #9a9a9a;
  font-size: 14px;
  line-height: 1;
  margin-bottom: 5px;
}

.card-stats .card-title {
  margin: 0;
  color: #fff;
  font-size: 1.5em;
  font-weight: 400;
  line-height: 1.2;
}


.error-table ::v-deep td {
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-copy-btn {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: color 0.15s;
}

.error-copy-btn:hover {
  color: #e14eca;
}

.cart-nowrap {
  white-space: nowrap;
}
.cart-contact {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  margin-top: 2px;
}
.cart-contact > .tim-icons:first-child {
  font-size: 11px;
  opacity: 0.7;
}
.cart-contact .error-copy-btn {
  font-size: 12px;
}
.cart-items {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cart-items__rest {
  color: rgba(255, 255, 255, 0.85);
}
.cart-total {
  font-weight: 700;
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.error-table ::v-deep .error-message-code {
  white-space: normal;
  min-width: 280px;
}
.white-content .cart-items__rest {
  color: rgba(29, 37, 59, 0.85);
}

.error-table ::v-deep .error-row-resolved td {
  opacity: 0.5;
}

.error-table ::v-deep .error-row-resolved:hover td {
  opacity: 0.75;
}

/* Light mode overrides */

.white-content .error-copy-btn {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .error-copy-btn:hover {
  color: #e14eca;
}


</style>
